import type { Vec2 } from '../../../geo/types';
import { emitFacade, type FacadeContext } from '../houseFacades';
import { panelWall } from '../panels';
import type { Opening } from '../openings';
import { edgeFace, edgePanel } from './masonry';
import { archPath, arcadeOutline, type Arch } from './arcade';
import { civicGable, civicOpenings, storeyLineNear } from './civicOpenings';
import type { EntranceWall } from './genericBlock';

/**
 * The first Waag on the Dam, in use from 1341 until 1561. Its form is unknown, and neither the
 * 1538 painting nor the 1544 woodcut draws a weighhouse apart from the houses around the Dam,
 * so it takes the form of the type: a tall hall for the scales, opened to the square by large
 * arched doorways, with a storey of windows over it, as its successor of 1561-1565 on the same
 * square had two storeys and doors for the scales in every side. An estimate, at the
 * landmark's low confidence.
 */
export const WEIGHHOUSE = {
  /** The hall's share of the eaves height, rounded to a storey line. */
  hallShare: 0.6,
  doorway: {
    width: 2.6,
    /** Least wall between two doorways. */
    pier: 1.2,
    /** Wall left between an arch's crown and the storey above the hall. */
    crownGap: 0.6,
  },
} as const;

/** As many doorways as fit with a pier between them, spread evenly along the wall. */
export function hallDoorways(length: number, hallY: number): Arch[] {
  const { width, pier, crownGap } = WEIGHHOUSE.doorway;
  const count = Math.max(1, Math.floor((length - pier) / (width + pier)));
  const spring = hallY - crownGap - width / 2;
  return Array.from({ length: count }, (_, k) => ({
    center: (length * (k + 0.5)) / count,
    width,
    spring,
  }));
}

function lifted(openings: readonly Opening[], by: number): Opening[] {
  return openings.map((o) => ({ ...o, y: o.y - by }));
}

/**
 * The hall's wall with the doorways cut up from its foot and closed by their doors, then the
 * storey over it opened as any civic wall, with the gable's windows when it is a gable end.
 */
export const weighhouseEntrance: EntranceWall = (civic, styles, edge, spec, apexY) => {
  const { builder, frame } = civic.facade;
  const [from, to]: readonly [Vec2, Vec2] = edge;
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const hallY = storeyLineNear(spec.eaveY, WEIGHHOUSE.hallShare);
  const doorways = hallDoorways(length, hallY);
  const panel = edgePanel(from, to);
  panelWall(
    builder,
    { wall: styles.wall, opening: styles.wall },
    frame,
    panel,
    arcadeOutline(length, hallY, doorways),
    []
  );
  const leaf = civic.facade.styles.leaf;
  for (const doorway of doorways)
    panelWall(builder, { wall: leaf, opening: leaf }, frame, panel, archPath(doorway), []);

  const wall = { length, y0: hallY, y1: spec.eaveY, eaveY: spec.eaveY, door: false, keepClear: [] };
  const windows = civicOpenings(wall, civic.rng.fork('entrance'));
  const gable = apexY === null ? null : lifted(civicGable(length, spec.eaveY, spec.pitch), hallY);
  const ctx: FacadeContext = {
    ...civic.facade,
    styles: { ...civic.facade.styles, wall: styles.wall },
  };
  const size = { width: length, eaves: spec.eaveY - hallY, ridge: (apexY ?? spec.eaveY) - hallY };
  emitFacade(ctx, edgeFace(from, to, hallY), size, { wall: lifted(windows, hallY), gable });
};
